import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { completeTour, fetchTourStates, restartTour, skipTour, type TourState } from "./onboarding-api";

export const ONBOARDING_TOURS_QUERY_KEY = ["onboarding", "tours"] as const;

export function useOnboardingToursQuery(enabled: boolean) {
  return useQuery({
    queryKey: ONBOARDING_TOURS_QUERY_KEY,
    queryFn: fetchTourStates,
    enabled,
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Complete / skip / restart mutations. Each optimistically patches the cached state so a finished tour
 * never re-triggers before the refetch lands; the server stays the source of truth (invalidated on settle).
 */
export function useOnboardingMutations() {
  const queryClient = useQueryClient();

  const patchLocal = (tourId: string, patch: Partial<TourState>) => {
    queryClient.setQueryData<TourState[]>(ONBOARDING_TOURS_QUERY_KEY, (old) => {
      const list = old ?? [];
      const existing = list.find((tour) => tour.tourId === tourId);
      const base: TourState = existing ?? { tourId, completedAt: null, skippedAt: null, restartCount: 0 };
      return [...list.filter((tour) => tour.tourId !== tourId), { ...base, ...patch }];
    });
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ONBOARDING_TOURS_QUERY_KEY });

  const complete = useMutation({
    mutationFn: (tourId: string) => completeTour(tourId),
    onMutate: (tourId) => patchLocal(tourId, { completedAt: new Date().toISOString(), skippedAt: null }),
    onSettled: invalidate
  });

  const skip = useMutation({
    mutationFn: (tourId: string) => skipTour(tourId),
    onMutate: (tourId) => patchLocal(tourId, { skippedAt: new Date().toISOString() }),
    onSettled: invalidate
  });

  const restart = useMutation({
    mutationFn: (tourId: string) => restartTour(tourId),
    onMutate: (tourId) => patchLocal(tourId, { completedAt: null, skippedAt: null }),
    onSettled: invalidate
  });

  return {
    completeTour: (tourId: string) => complete.mutate(tourId),
    skipTour: (tourId: string) => skip.mutate(tourId),
    restartTour: (tourId: string) => restart.mutateAsync(tourId)
  };
}
